import {useEffect, useState, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {Button, Spin, Table, Space, Popconfirm, message} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';

import {PlacementTestsService} from './PlacementTestsService';
import {PlacementTestSearch} from './PlacementTestSearch';
import Title from 'antd/es/typography/Title';

const placementTestsService = new PlacementTestsService();

export const PlacementTestsTable = () => {
  const [placementTests, setPlacementTests] = useState<Array<any> | []>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Search state management
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [placementTestsSearchResult, setPlacementTestsSearchResult] = useState<
    Array<any> | []
  >([]);
  const [searchPagination, setSearchPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    placementTestsService
      .getPlacementTests(1, 20)
      .then((resp) => {
        setPagination({
          current: resp.page,
          pageSize: 20,
          total: resp.total,
        });
        setIsLoading(false);
        setPlacementTests(resp.items);
      })
      .catch((error) => {
        setIsError(true);
        setIsLoading(false);
        console.log('Error fetching placement tests ->', error);
      });
  }, []);

  const getNextPlacementTests = async (nextPage: number) => {
    setIsLoading(true);
    placementTestsService
      .getPlacementTests(nextPage, 20)
      .then((resp) => {
        setPagination({
          current: resp.page,
          pageSize: 20,
          total: resp.total,
        });
        setIsLoading(false);
        setPlacementTests(resp.items);
      })
      .catch((error) => {
        setIsError(true);
        setIsLoading(false);
        console.log('Error fetching placement tests ->', error);
      });
  };

  // Handle search with new API
  const handleSearch = useCallback(async (term: string) => {
    if (term.length >= 3) {
      setIsSearching(true);
      setSearchTerm(term);
      try {
        const result = await placementTestsService.searchPlacementTestsByName(
          term,
          1,
          20,
        );
        setPlacementTestsSearchResult(result.items);
        setSearchPagination({
          current: result.page,
          pageSize: 20,
          total: result.total,
        });
      } catch (error) {
        console.error('Search error:', error);
        message.error('Failed to search placement tests');
      } finally {
        setIsSearching(false);
      }
    }
  }, []);

  // Handle search clear
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setPlacementTestsSearchResult([]);
    setSearchPagination({
      current: 1,
      pageSize: 20,
      total: 0,
    });
  }, []);

  // Handle search pagination
  const handleSearchPagination = async (page: number) => {
    if (searchTerm) {
      setIsSearching(true);
      try {
        const result = await placementTestsService.searchPlacementTestsByName(
          searchTerm,
          page,
          20,
        );
        setPlacementTestsSearchResult(result.items);
        setSearchPagination({
          current: result.page,
          pageSize: 20,
          total: result.total,
        });
      } catch (error) {
        console.error('Search pagination error:', error);
        message.error('Failed to load search results');
      } finally {
        setIsSearching(false);
      }
    }
  };

  const handleTableChange = async (pagination: any, filters, sorter) => {
    if (searchTerm) {
      // If searching, handle search pagination
      await handleSearchPagination(pagination.current);
    } else {
      // Otherwise, handle regular pagination
      await getNextPlacementTests(pagination.current);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await placementTestsService.deletePlacementTest(id);
      message.success('Placement test deleted successfully');

      // Always refresh both data sources to keep them in sync
      if (searchTerm) {
        // Refresh search results
        await handleSearch(searchTerm);
      }
      // Also refresh the main list to keep it in sync
      await getNextPlacementTests(pagination.current);
    } catch (error) {
      message.error('Failed to delete placement test');
      console.error('Delete error:', error);
    }
  };

  const columns: any = [
    {
      title: 'ID',
      dataIndex: 'id',
      sorter: (a: any, b: any) => a.id.length - b.id.length,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (id: string) => <Link to={'/placement-tests/' + id}>{id}</Link>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: any, b: any) => {
        return a?.name.localeCompare(b?.name);
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Description',
      dataIndex: 'description',
      sorter: (a: any, b: any) => {
        return (a?.description || '').localeCompare(b?.description || '');
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (description: string) => description || '-',
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      sorter: (a: any, b: any) => {
        return (a?.duration || '').localeCompare(b?.duration || '');
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (duration: string) => duration || '-',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      sorter: (a: any, b: any) => {
        return (a?.startDate || '').localeCompare(b?.startDate || '');
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (startDate: string) =>
        startDate ? new Date(startDate).toLocaleDateString() : '-',
    },
    {
      title: 'Expiration Date',
      dataIndex: 'expirationDate',
      sorter: (a: any, b: any) => {
        return (a?.expirationDate || '').localeCompare(b?.expirationDate || '');
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (expirationDate: string) =>
        expirationDate ? new Date(expirationDate).toLocaleDateString() : '-',
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      sorter: (a: any, b: any) => {
        return a?.createdAt.localeCompare(b?.createdAt);
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (createdAt: string) => new Date(createdAt).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'id',
      render: (id: string) => (
        <Space>
          <Link to={'/placement-tests/' + id}>
            <Button>View Details</Button>
          </Link>
          <Link to={'/placement-tests/' + id + '/edit'}>
            <Button type="default">Edit</Button>
          </Link>
          {/* <Popconfirm
            title="Are you sure you want to delete this placement test?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(id)}>
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm> */}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button size={'large'} style={{float: 'right', margin: 20}}>
        <Link to={'/placement-tests/create'}>Create</Link>
      </Button>
      <PlacementTestSearch
        onSearch={handleSearch}
        onClear={handleClearSearch}
        isLoading={isSearching}
      />
      {!isError && !isLoading && (
        <Table
          pagination={searchTerm ? searchPagination : pagination}
          columns={columns}
          dataSource={
            placementTestsSearchResult.length !== 0
              ? placementTestsSearchResult?.map((test) => ({
                  ...test,
                  key: test.id,
                }))
              : placementTests?.map((test) => ({...test, key: test.id}))
          }
          rowKey={(record) => record.id}
          onChange={handleTableChange}
          loading={isSearching || isLoading}
        />
      )}
      {isLoading && !isSearching && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Placement Tests fetching error ⚠️</Title>}
    </>
  );
};
